import { FaCalendarAlt, FaBullhorn, FaVideo, FaExternalLinkAlt, FaYoutube, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './Events.module.scss';
import NewsletterForm from '../../components/Newsletterform';
import { useState, useEffect, useMemo } from 'react';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import PageHero from '../../components/PageHero/PageHero';
import { PAGE_SEO } from '../../content/site';
// Imported rather than referenced as a root-absolute '/assets/...' URL, which
// is what the old hero did -- there is no public/assets/ directory, so it
// 404'd. Renamed from "banne25 (1).webp": the space and parentheses made the
// emitted filename unusable in an unquoted CSS url().
import heroImage from '../../assets/banne25-hero.webp';

type Event = {
  title?: string;
  date?: string;
  time?: string;
  location?: string;
  type?: string;
  status?: string;
  description?: string;
  attendees?: string;
  link?: string;
  ourPresence?: string;
  eventID?: string;
  eventDateTime?: string;
  datePassed?: boolean;
  active?: boolean;
}

const EventsPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Event API integration
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  // CRA env var
  const apiBase = useMemo(() => {
    const raw = process.env.REACT_APP_EVENTS_API || '';
    return raw.trim().replace(/\/$/, '');
  }, []);

  // Load events from API
  useEffect(() => {
    if (!apiBase) {
      setEventsError('Missing environment variable: REACT_APP_EVENTS_API');
      return;
    }

    const controller = new AbortController();

    const loadEvents = async () => {
      try {
        setEventsLoading(true);
        setEventsError(null);

        const url = `${apiBase}/events`;
        const res = await fetch(url, {
          method: `GET`,
          signal: controller.signal
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Events API error (${res.status}): ${text || res.statusText}`);
        }

        const data = await res.json();
        const normalized = 
          data?.body && typeof data.body === 'string'
            ? JSON.parse(data.body)
            : data;
        
        const items: Event[] = Array.isArray(normalized) ? normalized : [];
        const activeOnly = items.some(i => typeof i.active === 'boolean')
          ? items.filter(i => i.active)
          : items;

        setEvents(activeOnly);
        
      } catch (err: any) {
        if (err?.name ==='AbortError') return;
        console.error('Failed to load events:', err);
        setEventsError(err?.message || 'Failed to load events');
      } finally {
        setEventsLoading(false);
      }
      };

      loadEvents()
      return () => controller.abort();
      }, [apiBase]);

  useEffect(() => {
    // Trigger the animation when component mounts
    setIsVisible(true);
  }, []);

  // Helper: event title
  const eventTitle = (event: Event) => event.title || 'Untitled Event';

  // Helper: stable key
  const eventKey = (event: Event, idx: number) => event.eventID || event.title || `event-${idx}`;

  // Other helpers for fetching event details
  const eventDate = (event: Event) => new Date(event.eventDateTime || event.date|| '');
  const isUpcoming = (event: Event) => eventDate(event) >= new Date();
  const isPast = (event: Event) => event.datePassed === true || eventDate(event) < new Date();
  
  const upcomingEvents = [...events]
    .filter(isUpcoming)
    .sort((a, b) => eventDate(a).getTime() - eventDate(b).getTime());

  const pastEvents = [...events]
    .filter(isPast)
    .sort((a, b) => eventDate(b).getTime() - eventDate(a).getTime());

  return (
    <div className={styles.eventsPage}>
      <Seo {...seoPropsFor(PAGE_SEO.events)} />

      <div className={isVisible ? 'main-content' : ''}>
        <PageHero
          title="Upcoming & Past Events"
          subtitle="Join us at industry events and see our latest innovations"
          image={heroImage}
          tone="events"
        />

        {/* Upcoming Events */}
        <section className={styles.eventsContent} id="upcoming">
          <div className={styles.container}>
            <div className={styles.sectionHeader}>
              <h2><FaCalendarAlt className={styles.icon} />Upcoming Events</h2>
              <p>Meet us at these upcoming industry events and learning opportunities.</p>
            </div>

            {eventsLoading && <p>Loading events...</p>}
            {eventsError && <p style={{color:'#b00020'}}>{eventsError}</p>}
            {!eventsLoading && !eventsError && upcomingEvents.length === 0 && <p>No upcoming events found.</p>}

            <div className={styles.eventsGrid}>
              {upcomingEvents.map(event => (
                <div className={styles.eventCard} key={event.eventID}>
                  <div className={styles.eventDate}>
                    {(() => {
                      const d = eventDate(event);
                      return (
                        <>
                          <span className={styles.month}>{d.toLocaleString('default', { month: 'short' })}</span>
                          <span className={styles.day}>{d.getDate()}</span>
                          <span className={styles.year}>{d.getFullYear()}</span>
                        </>
                      );
                    })()}
                  </div>
                  <div className={styles.eventDetails}>
                    <div className={styles.eventBadges}>
                      {event.status && (
                        <span className={`${styles.badge} ${styles[event.status.toLowerCase()]}`}>
                          {event.status}
                        </span>
                      )}
                      <span className={`${styles.badge} ${styles.type}`}>
                        {event.type}
                      </span>
                    </div>
                    <h3>{event.title}</h3>
                    <div className={styles.eventMeta}>
                      <div className={styles.metaItem}>
                        <FaCalendarAlt className={styles.metaIcon} />
                        <span>{eventDate(event).toLocaleDateString()} • {event.time || 'Time TBD'}</span>
                      </div>
                      <div className={styles.metaItem}>
                        {event.location && event.location.includes("Virtual") ? (
                            <FaVideo className={styles.metaIcon} />
                        ) : (
                            <FaMapMarkerAlt className={styles.metaIcon} />
                        )}
                        <span>{event.location || 'Location TBD'}</span>
                      </div>
                    </div>
                    <p className={styles.eventDescription}>{event.description}</p>
                    <div className={styles.eventPresence}>
                      <strong>Our Presence:</strong> {event.ourPresence}
                    </div>
                    <div className={styles.eventActions}>
                      {event.link && (
                        <a href={event.link} target="_blank" rel="noopener noreferrer" className={styles.btnPrimary}>
                          Register Now
                          </a>
                      )}
                      {event.link && (
                        <a href={event.link} target="_blank" rel="noopener noreferrer" className={styles.btnSecondary}>
                          <FaExternalLinkAlt /> Event Website
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Past Events */}
        <section className={`${styles.eventsContent} ${styles.pastEvents}`} id="past">
          <div className={styles.container}>
            <div className={styles.sectionHeader}>
              <h2><FaCalendarAlt className={styles.icon} /> Past Events</h2>
              <p>Recent events where we've shared our expertise and connected with the community.</p>
            </div>

            {/* Desktop Table View */}
            <div className={`${styles.pastEventsTable} ${styles.desktopView}`}>
              <table>
                <thead>
                  <tr>
                    <th>Event Title</th>
                    <th>Date</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {pastEvents.map((event, index) => (
                    <tr key={index}>
                      <td className={styles.eventName}>{event.title}</td>
                      <td>{eventDate(event).toLocaleDateString()}</td>
                      <td>
                        <div className={styles.location}>
                          <FaMapMarkerAlt className={styles.metaIcon} />
                          {event.location}
                        </div>
                      </td>
                      <td>
                        <span className={styles.typeBadge}>{event.type}</span>
                      </td>
                      <td className={styles.eventDescription}>{event.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className={`${styles.pastEventsCards} ${styles.mobileView}`}>
              {pastEvents.map((event, index) => (
                <div className={styles.pastEventCard} key={index}>
                  <div className={styles.pastEventHeader}>
                    <h3>{event.title}</h3>
                    <span className={styles.typeBadge}>{event.type}</span>
                  </div>
                  <div className={styles.pastEventMeta}>
                    <div className={styles.metaItem}>
                      <FaCalendarAlt className={styles.metaIcon} />
                      <span>{eventDate(event).toLocaleDateString()}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <FaMapMarkerAlt className={styles.metaIcon} />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <p className={styles.eventDescription}>{event.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className={styles.newsletter} id="newsletter">
          <div className={styles.container}>
            <h2>Stay Informed About Our Events</h2>
            <p style={{ color: 'white' }}>
              Subscribe to our newsletter to receive notifications about upcoming events, 
              webinars, and speaking opportunities where you can connect with our team.
            </p>
            <div className={styles.newsletterForm}>
              <NewsletterForm />
            </div>
          </div>
        </section>
      </div>

      
    </div>
  );
};

export default EventsPage;
